import { useCallback } from "react";

import type { OpenTask } from "@storyboard/dispatch-view/task";
import type { DispatchGrain } from "@storyboard/dispatch-view/view";
import type { TaskSubjectWrite } from "@storyboard/web-client";
import {
  recordTaskPinBoards,
  recordTasks,
  type TaskIntent,
  taskPinBoardWriteFor,
  taskWriteFor,
} from "@/models/task/taskWrites";
import type { DispatchTableRow } from "./dispatchTableRow";
import { movedTaskExpiry, openTaskOfTarget, type TaskAbout, type TaskFold, taskGrainFor, taskWritesFor } from "./tasks";

/** What a new task carries beyond its subject: the board, the note, and the expiry, each null for none. */
export type NewTaskIntent = Omit<TaskIntent, "subjects">;

/** Who makes the tasks, and where: what every task write on the page is stamped with. */
export type TaskScope = { workspaceId: string; membershipId: string | undefined };

/**
 * The task writes the page makes: new tasks from rows or a History entry, and a task moved to another board.
 *
 * A bulk selection writes one batch. A message or event that already carries an open task gets that task moved to the
 * chosen board instead of a second task; a driver can carry many. A task made from rows is about each row's message
 * or event, or its driver, as the dispatcher chose; one from a History entry names the subject it is handed, whatever
 * the view's grain. A task closes by being acknowledged, which the page's acknowledgement writer does, or by its
 * expiry passing. Every pin written, a move's included, carries the expiry chosen with it, which replaces the one in
 * force, so a move also changes or removes the expiry. The note goes only on a new task.
 *
 * @param grain - The view's grain, which decides what a row's task is about.
 * @param scope - Who makes the tasks, and where.
 * @param fold - The workspace's open tasks, to find a row's task already in force.
 */
export function useTasks(
  grain: DispatchGrain,
  scope: TaskScope,
  fold: TaskFold,
): {
  makeTasks: (rows: readonly DispatchTableRow[], about: TaskAbout, intent: NewTaskIntent) => void;
  makeTaskFor: (subject: TaskSubjectWrite, intent: NewTaskIntent) => void;
  /** Pins a task to a board with the expiry given, null for none: a move, an expiry change, or both. */
  moveTask: (task: OpenTask, viewId: string, expiresAt: string | null) => void;
} {
  const { workspaceId, membershipId } = scope;
  const writer = useCallback(
    () => ({ workspaceId, actorWorkspaceMembershipId: membershipId ?? null }),
    [workspaceId, membershipId],
  );

  const makeTasks = useCallback(
    (rows: readonly DispatchTableRow[], about: TaskAbout, intent: NewTaskIntent) => {
      const { viewId, expiresAt } = intent;
      const { subjects, moves } = taskWritesFor(rows, taskGrainFor(grain, about), fold);
      recordTasks(
        writer(),
        subjects.map((subject) => taskWriteFor({ subjects: [subject], ...intent })),
      );
      if (viewId !== null) {
        recordTaskPinBoards(
          writer(),
          moves.map((moved) =>
            taskPinBoardWriteFor({
              taskId: moved.task.id,
              viewId,
              expiresAt: movedTaskExpiry(moved, expiresAt, rows.length),
            }),
          ),
        );
      }
    },
    [grain, fold, writer],
  );

  const makeTaskFor = useCallback(
    (subject: TaskSubjectWrite, intent: NewTaskIntent) => {
      const { viewId, expiresAt } = intent;
      // A message or event that already carries an open task has that task moved; a driver gets another.
      const current = subject.subject === "membership" ? null : openTaskOfTarget(subject, fold);
      if (current === null) {
        recordTasks(writer(), [taskWriteFor({ subjects: [subject], ...intent })]);
        return;
      }
      if (viewId !== null) {
        recordTaskPinBoards(writer(), [taskPinBoardWriteFor({ taskId: current.task.id, viewId, expiresAt })]);
      }
    },
    [fold, writer],
  );

  const moveTask = useCallback(
    (task: OpenTask, viewId: string, expiresAt: string | null) =>
      recordTaskPinBoards(writer(), [taskPinBoardWriteFor({ taskId: task.task.id, viewId, expiresAt })]),
    [writer],
  );

  return { makeTasks, makeTaskFor, moveTask };
}
